import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { DataTableService } from 'src/app/service/data-table.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableFilter, DataTableRow, FilterType } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { Parameter, Plan, PlanInfo, StepValidation, STEP_VALIDATION_TYPE } from './../../model/Plan';
import { EventService } from './../../service/event.service';
import { PlanService } from './../../service/plan.service';
import { PlansDuplicateComponent } from '../dialogs/plans-duplicate/plans-duplicate.component';
import { Events, MsgPayLoad } from 'src/app/service/websocket.service';
import { DataHeaderBarMenuItem } from '../controls/dataheaderbar/dataheaderbar.component';
import { ModalRef } from '../controls/modal/modal-ref';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { BaseComponent } from 'src/app/model/BaseComponent';
import { SolutionService } from 'src/app/service/solution.service';

export class PlanValidation {
  isOk: boolean;
  reason: string;
}

@Component({
    selector: 'app-plans-overview',
    templateUrl: './plans-overview.component.html',
    styleUrls: ['./plans-overview.component.css'],
    standalone: false
})
export class PlansOverviewComponent extends BaseComponent implements OnInit {
  cols: DataTableColumn[] = [];
  rows: DataTableRow[] = [];
  total: number = 0;
  planInfo: PlanInfo = new PlanInfo();
  loading: boolean = false;
  title: string = "";
  tableId: string = 'plans-table';
  filters: DataTableFilter[] = [];
  itemsPerPage: number = PageCount.TWENTYFIVE;
  currentPage: number = 1;
  filterOpen: boolean = false;
  filterApplied: boolean = false;
  dataHeaderBarMenus:DataHeaderBarMenuItem[] = [
      {id: 'new', name: $localize`New`},
      {id: 'import', name: $localize`Import`}
  ];

  //subscription
  solutionInfoSubscription: Subscription;
  filterSubscription: Subscription;
  applyFilterSubscription: Subscription;
  cancelFilterSubscription: Subscription;
  
  constructor(private planSrv: PlanService, protected eventService: EventService,
    private router: Router, private route: ActivatedRoute, private dataTableSrv: DataTableService,
    protected modal: Modal, protected authService:AuthenticationService, protected solutionService: SolutionService) {
    super(eventService, modal, authService, solutionService);
    this.cols = Array<DataTableColumn>(
      new DataTableColumn("name", $localize`Name`, ColumnType.STRING, false, "21.5"),
      new DataTableColumn("selectedEnvironmentName", $localize`Environment`, ColumnType.STRING, false, "21.5"),
      new DataTableColumn("createdBy", $localize`Created By`, ColumnType.STRING, false, "15"),
      new DataTableColumn("updatedBy", $localize`Updated By`, ColumnType.STRING, false, "15"),
      new DataTableColumn("createdDateTime", $localize`Created`, ColumnType.DATEFROMNOW, false, "13.5"),
      new DataTableColumn("updatedDateTime", $localize`Updated`, ColumnType.DATEFROMNOW, true, "13.5", ColumnSort.DESC)
    );

    this.filters = Array<DataTableFilter>(
      new DataTableFilter("name-filter-query", "query", $localize`Search by plan name`, FilterType.STRING, undefined)
    );

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.title = $localize`${this.solutionInfo.currentEnvironment.solName} : ${this.solutionInfo.currentEnvironment.name}`
      this.getPlans(this.itemsPerPage, 0);
    });

    this.filterSubscription = this.dataTableSrv.filterClicked$.subscribe(() => {
      this.filterOpen = !this.filterOpen;
      if (!this.filterOpen && this.filterApplied) {
        this.cancelFilter();
      }
    });

    this.applyFilterSubscription = this.dataTableSrv.applyFilter$.subscribe((appliedFilters: DataTableFilter[]) => {
      this.getPlans(this.itemsPerPage, 0, appliedFilters[0].selected);
      this.filterApplied = true;
    });

    this.cancelFilterSubscription = this.dataTableSrv.cancelFilter$.subscribe(() => {
      this.getPlans(this.itemsPerPage, 0);
    });
   }

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.loading = true;
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
    this.filterSubscription.unsubscribe();
    this.applyFilterSubscription.unsubscribe();
    this.cancelFilterSubscription.unsubscribe();
  }

  handleDataHeaderBarMenu(menu:DataHeaderBarMenuItem){
    if(menu.id == 'new'){
      this.router.navigate(['/plans/new']);
    }else if(menu.id == 'import'){
      this.router.navigate(['/plans/import']);
    }
  }

  cancelFilter(): void {
    this.getPlans(this.itemsPerPage, 0);
    this.filterApplied = false;
  }

  onPageChange(pageEvent: PageEvent){
    this.itemsPerPage = pageEvent.itemsPerPage;
    this.currentPage = pageEvent.pagenum;
    this.getPlans(pageEvent.itemsPerPage, pageEvent.startingIndex);
  }

  validatePlan(plan: Plan): PlanValidation {
    let vType:StepValidation = {type: STEP_VALIDATION_TYPE.VALID, reason: ''};
    let planValidationResult:PlanValidation = {isOk: true, reason: ''};
    let validatedSteps:number = 0;

    for(var j = 0; j < plan.steps.length; j++){
      //enabled steps
      if(!plan.steps[j].disabled){
        //check each parameters
        let parameters:Parameter[] = Object.values(plan.steps[j].parameters);

        for(let i =0; i < parameters.length; i++){
          if(parameters[i].visible && parameters[i].required && !parameters[i].sensitive && (
            (parameters[i].value === undefined || parameters[i].value === '') &&
            (parameters[i].valueList === undefined || parameters[i].valueList.length == 0) &&
            (parameters[i].objectList === undefined || parameters[i].objectList.length == 0))){
              let applyError: boolean = true;
              if(parameters[i].variableName != undefined && parameters[i].variableName != ''){
                for(let j =0; j < plan.variables.length; j++){
                  if(plan.variables[j].name === parameters[i].variableName){
                    if(plan.variables[j].sensitive){
                      applyError = false;
                    }
                    break;
                  }
                }
              }
              if(applyError){
                vType = {type: STEP_VALIDATION_TYPE.REQ_PARAM_MISSING_ERROR, reason: $localize`${parameters[i].displayName} value in ${parameters[i].group == '' ? 'Default' : parameters[i].group} group is missing from ${plan.steps[j].nickname} step`};
              }
            break;
          }
        };
        validatedSteps++;
      }
      
      if(vType.type != STEP_VALIDATION_TYPE.VALID){
        break;
      }
    }
    
    if(validatedSteps > 0){
      planValidationResult.isOk = vType.type == STEP_VALIDATION_TYPE.VALID;
      planValidationResult.reason = vType.reason;
    }else{
      planValidationResult.isOk = false;
      planValidationResult.reason = $localize`All steps are disabled`;
    }


    return planValidationResult;
  }
  
  actionMenuClicked(event: ActionMenuEvent): void{
    switch (event.name) {
      case "Duplicate": {
        //copy to the current environment
        let planToDuplicate:Plan = JSON.parse(JSON.stringify(event.data[0]));
        planToDuplicate.selectedEnvironmentId = this.solutionInfo.currentEnvironment.id;
        planToDuplicate.selectedEnvironmentName = this.solutionInfo.currentEnvironment.name;
        
        const sub: Subscription =
          this.modal.open(PlansDuplicateComponent, {
            data: {
              plan: planToDuplicate
            },
            id: 'otsc-plans-duplicate'
          }).afterClosed.subscribe((result: any) => {
            if (result.isPrimary) {
              this.planSrv.savePlan(result.plan).pipe(
                tap(resp => {
                  if (resp.body.id > 0) {
                    Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`The plan was duplicated successfully`);
                  } else {
                    Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`The plan could not be duplicated. Please try again later.`);
                  }
                }),
                finalize(() => {
                  this.getPlans(this.itemsPerPage, 0);
                })
              ).subscribe();
            }
            sub.unsubscribe();
          });
        break;
      }
      case "Export": {
        const sub: Subscription =
          this.modal.open(ModalMessageComponent, {
            data: {
              title: $localize`Export plan`,
              message: $localize`Do you want to export <b>${event.data[0].name}</b>?`,
              primaryBtn: $localize`Yes`,
              secondaryBtn: $localize`No`,
              width: '350px',
            },
            id: 'otsc-plans-export'
          }
          ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
            if (isPrimaryBtnClicked) {
              window.open('/export/plan?planId=' + event.data[0].id, "_blank");
            }
            sub.unsubscribe();
          });
        break;
      }
      case "Edit": {
        this.router.navigate(['/plans/details/' + event.data[0].id]);
        break;
      }
      case "Execute": {
        if(Utils.isSystemUpdateForced(this.solutionInfo)){
          Utils.openUpdateRequiredDialog(this.modal);
        }else{
          //validate the plan
          let validateModalRef: ModalRef = this.modal.open(ModalMessageComponent, {
            data: {
                        title: $localize`Verifying the plan. Please wait...`,
                        message: '',
                        primaryBtn: $localize`Cancel`,
                        type: ModalMessageType.Information,
                        width: '350px'
                      },
                id: 'otsc-plans-verification-msg'
          });

          let planValidationResult:PlanValidation = this.validatePlan(event.data[0]);

          setTimeout(() => {
            validateModalRef.close();

            if(planValidationResult.isOk){
              const sub: Subscription =
                this.modal.open(ModalMessageComponent, {
                  data: {
                    title: $localize`Execute`,
                    message: $localize`The selected plan <b>${event.data[0].name}</b> will be validated. Would you like to continue?`,
                    primaryBtn: $localize`Continue`,
                    secondaryBtn: $localize`Cancel`,
                    type: ModalMessageType.Question,
                    width: '450px'
                  },
                  id: 'otsc-plans-execute-msg'
                }
                ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
                  if (isPrimaryBtnClicked) {
                    this.router.navigate(['/plans/execute/' + event.data[0].id]);
                  }
                  sub.unsubscribe();
                });
            }else{
              const sub: Subscription =
                this.modal.open(ModalMessageComponent, {
                  data: {
                    title: $localize`Warning`,
                    message: $localize`${planValidationResult.reason}. Edit selected plan?`,
                    primaryBtn: $localize`Edit`,
                    secondaryBtn: $localize`Cancel`,
                    type: ModalMessageType.Warning,
                    width: '550px'
                  },
                  id: 'otsc-plans-missing-param-msg'
                }
                ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
                  if (isPrimaryBtnClicked) {
                    this.router.navigate(['/plans/details/' + event.data[0].id]);
                  }
                  sub.unsubscribe();
                });
            }
          }, 500);
        }
        break;
      }
      case "Delete": {
        const sub: Subscription =
          this.modal.open(ModalMessageComponent, {
            data: {
              title: $localize`Remove`,
              message: event.data.length === 1 ? $localize`<b>${event.data[0].name}</b> will be permanently removed. Remove selected plan?` :
                $localize`${event.data.length} plans will be permanently removed. Remove selected plans?`,
              primaryBtn: $localize`Remove`,
              secondaryBtn: $localize`Cancel`,
              width: '350px'
            },
            id: 'otsc-plans-remove-msg'
          }
          ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
            if (isPrimaryBtnClicked) {
              let plansToDelete: number[] = [];

              event.data.forEach((data) => {
                plansToDelete.push(data.id);
              });

              this.planSrv.deletePlan(plansToDelete).pipe(
                tap(resp => {
                  let msg;

                  if (resp.body) {
                    msg = new MsgPayLoad(undefined, {
                      message: event.data.length === 1 ? $localize`${event.data[0].name} was successfully removed.` :
                        $localize`${event.data.length} plans were successfully removed.`
                    });
                    msg.setEvent(Events.SuccessMsg);
                  } else {
                    msg = new MsgPayLoad(undefined, {
                      message: event.data.length === 1 ? $localize`The server encountered an error while removing the selected plan. Please try again later or contact your system administrator.` :
                        $localize`The server encountered an error while removing the selected plans. Please try again later or contact your system administrator.`
                    });
                    msg.setEvent(Events.ErrorMsg);
                  }

                  Utils.addHeaderMessageBanner(this.eventService, msg);
                }),
                finalize(() => {
                  this.dataTableSrv.onResetPagination();
                  this.getPlans(this.itemsPerPage, 0);
                })
              ).subscribe();
            }
            sub.unsubscribe();
          });
        break;
      }
    }
  }

  getPlans(pageSize: number, startIndex: number, query?: string){
    this.loading = true;
    var tmpRows: DataTableRow[] = [];
    this.planSrv.getPlans(pageSize, this.solutionInfo.currentEnvironment.solName, startIndex, query).pipe(
      tap(resp => {
        this.rows = [];
        this.planInfo = resp.body;
        this.total = this.planInfo.count;
        this.planInfo.plans.forEach(plan =>{
          //users can only execute the plan in the same environment
          plan.canExec = this.solutionInfo.currentEnvironment.id == plan.selectedEnvironmentId;

          //default actions
          var actions = new Array<ActionMenuItem>(
            new ActionMenuItem("Duplicate", $localize`Duplicate`, ActionMenuIcon.COPY, plan, false),
            new ActionMenuItem("Export", $localize`Export`, ActionMenuIcon.DOWNLOAD, plan, false),
          );

          //can edit if has access
          if(plan.hasAccess){
            actions.push(new ActionMenuItem("Edit", $localize`Edit`, ActionMenuIcon.EDIT, plan, false));
          }

          //can exec if has access and can exec
          if(plan.hasAccess && plan.canExec){
            actions.push(new ActionMenuItem("Execute", $localize`Execute`,  ActionMenuIcon.EXECUTE, plan, false));
          }

          //can delete if has access
          if(plan.hasAccess){
            actions.push(new ActionMenuItem("Delete", $localize`Delete`,  ActionMenuIcon.DELETE, plan, true));
          }

          tmpRows.push(new DataTableRow(plan, actions));
        });
        this.rows = tmpRows;
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }
}